import * as L from 'leaflet';

/**
 * Turns a pointer trail into a boundary somebody can edit.
 *
 * A freehand drag reports a sample every few milliseconds, so tracing one patch
 * produces several hundred positions, most of them a fraction of a metre apart.
 * Kept as-is they would become several hundred draggable corners: the handles
 * would sit on top of each other, every shared-border scan would run over a ring
 * an order of magnitude longer than it needs to be, and nudging the shape would
 * be impossible because the corner you grabbed is one of forty describing the
 * same bend. Ramer-Douglas-Peucker keeps the corners that carry the shape and
 * drops the ones that only carry the tremor of the hand that drew it.
 *
 * The tolerance is in METRES, not pixels. A pixel tolerance would make the same
 * gesture produce a different boundary at every zoom - trace a patch zoomed out
 * and it keeps a handful of corners, trace the same patch zoomed in and it keeps
 * hundreds - and the boundary is a fact about the ground, not about the screen it
 * was drawn on.
 */

/**
 * How far a corner may sit from the line between its neighbours before it is
 * dropped.
 *
 * 75 m, and it was measured rather than reasoned about. The first guess was 25 m,
 * argued from a city block being 80-150 m across. Traced in the browser it left
 * 125 corners on one circle, because the argument had the wrong quantity in it:
 * what has to be removed is a pointer wobble, and a wobble is roughly constant in
 * PIXELS. Its size on the ground is therefore whatever the zoom says. Simplifying
 * the same 300-sample circle at Chicago's latitude with two pixels of tremor:
 *
 *     patch across   25 m   50 m   75 m   100 m   150 m
 *     53 km (z10)     254    186    158     129      99
 *     13 km (z12)     129     98     48      37      18
 *      3 km (z14)      37     17     17      17       9
 *
 * A metro sales patch is sketched at z12-z13, where 75 m is 3-5 px - clear of any
 * tremor - and the ring comes out in the low tens of corners, the same order as
 * one drawn by clicking. That is the target: both tools should hand the editor the
 * same kind of ring. 25 m is not a finer boundary at that size, it is the same
 * boundary wearing the hand that drew it.
 *
 * 75 m is still under a city block, so the original argument survives: a corner
 * turned deliberately at an intersection is further than that from the line
 * between its neighbours, and it stays. And it is far below the accuracy at which
 * a territory line is a decision - a door is assigned by point-in-polygon, and
 * the whole point of handing the ring to TerritoryBoundaryEditor is that the line
 * is then dragged exactly where it belongs.
 *
 * A bigger patch keeps more corners at the same tolerance. That is correct, not a
 * flaw: describing a 53 km outline to the same fidelity as a 3 km one takes more
 * of them.
 */
export const FreehandToleranceMeters = 75;

/**
 * The most corners a sketch may hand over. Past it the tolerance is doubled until
 * the ring fits.
 *
 * 80, and this line was measured too. Traced in the browser at z11 - a 26 km
 * patch, about as big as anybody sketches - the same circle came out at:
 *
 *     no tremor      33 corners
 *     1 px tremor    33 corners     (75 m absorbs it completely)
 *     2 px tremor    91 corners
 *
 * So the honest cost of the shape itself is in the thirties even for a large
 * patch, and a count far above that is the hand rather than the boundary. 80 sits
 * between: it never touches a ring whose corners are all doing work - a 53 km
 * outline needs about 65 and keeps them - and it catches the case where the
 * tolerance was outrun, which at very low zoom it can be, since a wobble is
 * constant in pixels and the tolerance is not.
 */
export const FreehandMaxVertices = 80;

/**
 * Metres per degree, the same pair the server uses for territory areas
 * (TerritoryDraftLogic.AreaSqKm), so a tolerance measured here means the same
 * distance the backend would report.
 */
const MetresPerDegreeLat = 110574;
const MetresPerDegreeLngAtEquator = 111320;

/**
 * Ramer-Douglas-Peucker over a traced path.
 *
 * The trace is an OPEN path - the ring closes by joining its last sample back to
 * its first - so both ends are anchors and plain RDP is exactly right. Simplifying
 * it as a closed ring instead would let the algorithm cut the corner the gesture
 * started and ended on, which is the one corner the person definitely meant.
 */
export function simplifyRing(
  path: readonly L.LatLng[],
  toleranceMeters: number,
): L.LatLng[] {
  const points = dropRepeats(path);
  if (points.length <= 2 || toleranceMeters <= 0) return points;

  // One local projection for the whole ring, about its own middle: a patch spans
  // a few tens of km, over which an equirectangular grid is accurate to well
  // under the tolerance itself.
  const lat0 = points.reduce((sum, p) => sum + p.lat, 0) / points.length;
  const metresPerDegreeLng = MetresPerDegreeLngAtEquator * Math.cos(lat0 * Math.PI / 180);
  const xy = points.map(p => ({ x: p.lng * metresPerDegreeLng, y: p.lat * MetresPerDegreeLat }));

  const keep = new Array<boolean>(points.length).fill(false);
  keep[0] = true;
  keep[points.length - 1] = true;
  divide(xy, 0, points.length - 1, toleranceMeters, keep);
  return points.filter((_, i) => keep[i]);
}

/**
 * Simplify, then keep simplifying until the ring is small enough to wear a handle
 * on every corner. Returns the ring and the tolerance that produced it, because a
 * caller that had to coarsen the sketch should be able to say so.
 */
export function simplifyRingWithinBudget(
  path: readonly L.LatLng[],
  toleranceMeters: number = FreehandToleranceMeters,
  maxVertices: number = FreehandMaxVertices,
): { ring: L.LatLng[]; toleranceMeters: number } {
  let tolerance = toleranceMeters;
  let ring = simplifyRing(path, tolerance);
  // Bounded: doubling ten times is a tolerance of 25 km, past which no further
  // doubling can be removing detail anybody drew.
  for (let i = 0; i < 10 && ring.length > maxVertices; i++) {
    tolerance *= 2;
    ring = simplifyRing(path, tolerance);
  }
  return { ring, toleranceMeters: tolerance };
}

/**
 * Consecutive duplicates removed, including the wrap from last back to first.
 *
 * Two kinds of duplicate arrive here. A pointer that stops moving keeps
 * reporting, and snapping can pull two neighbouring corners onto the same
 * coordinate of the same neighbour. Both produce a zero-length edge, which has no
 * direction to measure a distance against and which reads as a self-touch to any
 * validity check.
 */
export function dropRepeats(path: readonly L.LatLng[]): L.LatLng[] {
  const out: L.LatLng[] = [];
  for (const point of path) {
    const last = out[out.length - 1];
    if (!last || !identical(last, point)) out.push(point);
  }
  while (out.length > 1 && identical(out[0], out[out.length - 1])) out.pop();
  return out;
}

/** Same position to the last bit. Deliberately exact: SharedPointEpsilon is
 *  about two territories meaning the same corner, and this is about one sample
 *  arriving twice. */
function identical(a: L.LatLng, b: L.LatLng): boolean {
  return a.lat === b.lat && a.lng === b.lng;
}

interface Metres { x: number; y: number; }

/** The recursive half: keep the point furthest off the chord if it is further
 *  than the tolerance, and recurse into both halves it creates. */
function divide(
  xy: readonly Metres[],
  first: number,
  last: number,
  tolerance: number,
  keep: boolean[],
): void {
  if (last <= first + 1) return;

  let furthest = -1;
  let furthestDistance = tolerance;
  for (let i = first + 1; i < last; i++) {
    const distance = distanceToSegment(xy[i], xy[first], xy[last]);
    if (distance > furthestDistance) {
      furthestDistance = distance;
      furthest = i;
    }
  }
  if (furthest < 0) return;

  keep[furthest] = true;
  divide(xy, first, furthest, tolerance, keep);
  divide(xy, furthest, last, tolerance, keep);
}

/** Perpendicular distance in metres from `p` to segment `a`-`b`, clamped. */
function distanceToSegment(p: Metres, a: Metres, b: Metres): number {
  const dx = b.x - a.x;
  const dy = b.y - a.y;
  const lengthSq = dx * dx + dy * dy;
  if (lengthSq === 0) return Math.hypot(p.x - a.x, p.y - a.y);
  const t = Math.max(0, Math.min(1, ((p.x - a.x) * dx + (p.y - a.y) * dy) / lengthSq));
  return Math.hypot(p.x - (a.x + t * dx), p.y - (a.y + t * dy));
}
